import { PetEvolutionStage, PetSpeciesID } from '.';
import * as Pet from '.';
import { action } from '@play-co/replicant';
import { getRealLength } from 'src/replicant/utils/string';
import createActions from '../config/actions/createActions';
import {
  getAnalyticsAttributeUserProperties,
  getAnalyticsUserProperties,
  getCurrentEvolution,
  getNextRebornSpecies,
  getNextRedoSpeciesEvolution,
  getNextSpeciesEvolution,
} from './pet.getters';
import { acceptEvolution, createFirstPet, revivePet } from './pet.modifiers';
import * as Fate from '../fate';
import * as Level from '../level';
import * as History from '../history';
import * as Inventory from '../inventory';
import { NAME_LENGTH_CONSTRAINTS } from './pet.ruleset';
import { sendAnalyticsEvents } from '../analytics';

export const petActions = createActions({
  /**
   * Create new pet on first game session
   */
  createNewPet: action((state, _: void, api) => {
    createFirstPet(state, api.date.now(), () => api.math.random());
  }),

  /**
   * Reborn pet after death
   */
  rebornPet: action((state, _: void, api) => {
    // Get next reborn species
    const newPet = getNextRebornSpecies(state, api.math.random);
    const now = api.date.now();

    // Cleanup all pet dialogs counters on pet death, helps to reduce state size
    state.conversation.dialogCounts = {};
    state.conversation.answers = {};

    // Spawn new pet
    const data = Pet.modifiers.spawnPet(state, 'death', newPet, now);

    History.modifiers.addDeathRecord(state, {
      ...data,
      species: data.species!,
      timestamp: now,
    });
  }),

  /**
   * Update name
   */
  updateName: action((state, name: string, api) => {
    const length = getRealLength(name);
    if (length < NAME_LENGTH_CONSTRAINTS[0]) {
      throw new Error(
        `Pet name name must be more than ${NAME_LENGTH_CONSTRAINTS[0]} character`,
      );
    }
    if (length > NAME_LENGTH_CONSTRAINTS[1]) {
      throw new Error(
        `Pet name must be less than ${NAME_LENGTH_CONSTRAINTS[1]} characters`,
      );
    }

    state.pet.name = name;
    state.pet.nameUpdated = true;
  }),

  /**
   * Pet evolution action
   */
  evolvePet: action((state, _: void, api) => {
    const currentEvolution = getCurrentEvolution(state);
    if (!currentEvolution) {
      throw new Error('Invalid evolution attempt');
    }

    const nextEvolution = getNextSpeciesEvolution(state, api.math.random);
    if (!nextEvolution) {
      throw new Error(
        `Invalid evolution attempt from species ${currentEvolution.species}`,
      );
    }

    // Do not evolve if there is no evolving status effect
    const now = api.date.now();
    if (!Fate.getters.hasPermanentStatusEffect(state, 'evolving', now)) {
      throw new Error('No evolving status effect');
    }

    // Save current attributes value to state before change species
    // If we won't do that species attributes interpolation will be wrong
    Pet.modifiers.syncAttributes(state, now);

    const { id, stage } = nextEvolution;
    Pet.modifiers.addEvolution(state, id, stage, now);

    Fate.modifiers.resolveStatusEffect(state, 'evolving', false, now);

    // Determine new fate
    Fate.modifiers.update(state, now);

    // Initialize evolution redo counter.
    state.evolutionRewind = {
      count: 0,
      stage,
      species: id,
    };

    // Accept evolution by default.
    acceptEvolution(state, now);

    // Detect if pet in history
    const history = History.getters.getRaisedPetsHistory(state);
    const raised = history[id] ?? 0; // Pets raised count

    const timeFromLastEvolution = now - currentEvolution.timestamp;
    sendAnalyticsEvents(api, [
      {
        eventType: 'PetEvolution',
        eventProperties: {
          old_generation: currentEvolution.stage,
          new_generation: stage,
          old_pet_id: currentEvolution.species,
          new_pet_id: id,
          sec_from_last_evolution: Math.floor(timeFromLastEvolution / 1000),
          evolutionCount: state.pet.evolutions.length - 1,
          isNew: raised === 1,
          redos: 0,
        },
        userProperties: {
          ...getAnalyticsUserProperties(state, now),
          ...getAnalyticsAttributeUserProperties(state, now),
        },
      },
    ]);

    // Grant xp.
    const analyticsEvents = Level.modifiers.incrementXp(
      state,
      'evolution',
      stage,
    );
    sendAnalyticsEvents(api, analyticsEvents);
  }),

  // Reroll the current evolution
  redoEvolution: action((state, _: void, api) => {
    if (!state.evolutionRewind) {
      throw new Error(
        'Trying to redo evolution while rewind is not in progress',
      );
    }

    const now = api.date.now();

    // Save current attributes value to state before change species
    // If we won't do that species attributes interpolation will be wrong
    Pet.modifiers.syncAttributes(state, now);

    // Keep track of the current evolution. And remove it from the list.
    const currentEvolution = state.pet.evolutions.pop();

    if (!currentEvolution) {
      throw new Error('Trying to redo evolution without any evolutions');
    }

    // Save current evolution to rewind state.
    state.evolutionRewind.stage = currentEvolution.stage;
    state.evolutionRewind.species = currentEvolution.species;

    const cost = Pet.getters.getEvolutionRedoCost(state);
    if (cost > Inventory.getters.getGems(state)) {
      throw new Error('Cannot afford evolution redo!');
    }

    const nextEvolution = getNextRedoSpeciesEvolution(state, api.math.random);
    if (!nextEvolution) {
      throw new Error(
        `Invalid evolution attempt from species ${currentEvolution.species}`,
      );
    }

    const { id, stage } = nextEvolution;
    Pet.modifiers.addEvolution(state, id, stage, now);

    state.evolutionRewind.count++;

    // Determine new fate
    Fate.modifiers.update(state, now);

    Inventory.modifiers.consumeGems(
      state,
      cost,
      {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_' + String(cost).padStart(3),
      },
      api,
    );

    const timeFromLastEvolution = now - currentEvolution.timestamp;
    sendAnalyticsEvents(api, [
      {
        eventType: 'PetEvolution',
        eventProperties: {
          old_generation: currentEvolution.stage,
          new_generation: stage,
          old_pet_id: currentEvolution.species,
          new_pet_id: id,
          sec_from_last_evolution: Math.floor(timeFromLastEvolution / 1000),
          evolutionCount: state.pet.evolutions.length - 1,
          redos: state.evolutionRewind.count,
        },
        userProperties: {
          ...getAnalyticsUserProperties(state, now),
          ...getAnalyticsAttributeUserProperties(state, now),
        },
      },
    ]);
  }),

  acceptEvolution: action((state, discard: boolean, api) => {
    if (!state.evolutionRewind) {
      throw new Error(
        'Trying to accept evolution while rewind is not in progress',
      );
    }

    const now = api.date.now();
    if (discard) {
      state.pet.evolutions.pop();
      const { species, stage } = state.evolutionRewind;
      Pet.modifiers.addEvolution(
        state,
        species as PetSpeciesID,
        stage as PetEvolutionStage,
        now,
      );
    }

    acceptEvolution(state, now);
  }),

  /**
   * Revive the pet to its initiall attributes, only if dead
   */
  revivePet: action((state, _: void, api) => {
    return revivePet(state, api.date.now());
  }),

  resolveWantOut: action((state, _: void, api) => {
    Pet.modifiers.setStatusEffect(state, 'wantToGoOut', false, api.date.now());
  }),

  resolveWantInvite: action((state, _: void, api) => {
    const now = api.date.now();
    Pet.modifiers.setStatusEffect(state, 'wantToInvite', false, now);
    // Clear the pending schedule entry too — otherwise a past-timestamp
    // entry (Path 1 anchor on catch-up users) would survive the dismiss
    // and re-mature `value=true` on the next sync, undoing the user's
    // dismissal and re-firing the share on the next pet tap.
    delete state.fate.schedule.wantToInvite;
    // For catch-up users (V2 just enabled, never had a real fire), treat
    // the dismiss as the effective fire time so V2 Path 1 doesn't keep
    // re-firing on subsequent fate updates — Path 2's cooldown takes over.
    if (state.pet.lastWantToInviteFireAt === 0) {
      state.pet.lastWantToInviteFireAt = now;
    }
    // No Fate.modifiers.update call here. Calling sync now would
    // re-mature any schedule entry we just deleted (because the deletion
    // happens AFTER the loop scheduled it in the previous fate update).
    // Fate will recompute on the next natural trigger (care action,
    // periodic refresh, next conversation start, etc.).
  }),
});
